import type { SupabaseClient } from '@supabase/supabase-js';
import { normalizeSelectedPackages } from '../billing';
import { isComplianceTrialAccessScopeTenant } from '../compliance-trial-access';
import { TENANT_ROW_COLUMNS } from './tenant-columns';
import { fetchTenantModuleActivationSummaries } from './module-activations';
import { toCamel } from './to-camel';
import { supabase } from './query-client';

// ─── Tenant Config ──────────────────────────────────────────
export async function fetchTenantConfig(
  tenantId: string,
  client?: SupabaseClient,
  // PERF (2026-07-02 audit, Wave 3): an OPTIONAL, already-fetched tenants row.
  // When a server caller has already loaded the row via the request-scoped
  // getTenantRow() (which selects the IDENTICAL column set), pass it here to skip
  // a redundant, identical SELECT. Purely an optimization: when omitted/null the
  // function behaves exactly as before. Client callers never pass it.
  prefetchedTenantRow?: Record<string, unknown> | null,
) {
  // Accepts an optional server client so the dashboard layout can resolve the
  // same tenant config on the server (loading-UX phase 2) — defaults to the
  // browser singleton for the existing client-side callers.
  const sb = client ?? supabase();
  let data: Record<string, unknown> | null = prefetchedTenantRow ?? null;
  if (!data) {
    const { data: fetched, error } = await sb
      .from('tenants')
      .select(TENANT_ROW_COLUMNS)
      .eq('id', tenantId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching tenant config:', error);
      return null;
    }
    data = (fetched as Record<string, unknown> | null) ?? null;
  }
  if (!data) return null;
  const moduleActivations =
    data.is_demo === true ? [] : await fetchTenantModuleActivationSummaries(sb, tenantId);
  const camel = toCamel(data);
  const brandConfig = (camel.brandConfig as Record<string, unknown> | null) ?? null;
  const selectedPackages = normalizeSelectedPackages(camel.selectedPackages);
  const fallbackPackages = normalizeSelectedPackages(brandConfig?.selectedPackages);
  camel.selectedPackages = selectedPackages.length > 0 ? selectedPackages : fallbackPackages;
  camel.moduleActivations = moduleActivations;
  return camel;
}

// ─── Subscription Banner Data ─────────────────────────────
export async function fetchSubscriptionBanner(tenantId: string) {
  const sb = supabase();
  const { data, error } = await sb
    .from('tenants')
    .select(
      'billing_plan, subscription_status, trial_ends_at, declared_beds, declared_clients, is_demo, selected_packages, brand_config',
    )
    .eq('id', tenantId)
    .maybeSingle();
  if (error || !data) return null;

  const isComplianceTrial = isComplianceTrialAccessScopeTenant({
    billingPlan: data.billing_plan,
    subscriptionStatus: data.subscription_status,
    isDemo: data.is_demo,
    selectedPackages: data.selected_packages,
    brandConfig: data.brand_config,
  });

  if (isComplianceTrial) {
    return toCamel({
      subscription_status: data.subscription_status ?? 'trialing',
      trial_ends_at: data.trial_ends_at ?? null,
      declared_beds: data.declared_beds ?? 0,
      declared_clients: data.declared_clients ?? 0,
      actual_beds: 0,
      actual_clients: 0,
    });
  }

  // PERF (2026-07-02 audit, Wave 3): these two counts are independent (both keyed
  // only on tenantId) and were previously run sequentially on every shell mount —
  // the ACTIVE-clients head-count is one of the hottest queries in prod. Run them
  // in parallel to halve the banner's DB latency. The early return above still
  // short-circuits for a nonexistent tenant before either count runs.
  const [bedsResult, clientsResult] = await Promise.all([
    sb
      .from('rooms')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .eq('status', 'ACTIVE'),
    sb
      .from('clients')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .eq('status', 'ACTIVE')
      .is('deleted_at', null),
  ]);
  const actualBeds = bedsResult.count;
  const actualClients = clientsResult.count;

  return toCamel({
    subscription_status: data.subscription_status ?? 'trialing',
    trial_ends_at: data.trial_ends_at ?? null,
    declared_beds: data.declared_beds ?? 0,
    declared_clients: data.declared_clients ?? 0,
    actual_beds: bedsResult.error ? null : actualBeds,
    actual_clients: clientsResult.error ? null : actualClients,
  });
}
